import { createSelector } from '@ngrx/store';
import { BookingTariff, BookingTariffType, isBookingProductTypeTariff } from '@products/models/booking.model';
import { VoucherCode } from '@products/models/voucher.model';
import { getProductBookingState } from '@store/products/product.selectors';
import { cloneDeep } from 'lodash';

/**
 * Get all tariff types from bookings
 * @description Get all tariff types from bookings (package tariff types not included)
 * @return Tariff types or empty array
 */
export const getBookedTariffTypes = createSelector(
  getProductBookingState,
  (productBookingState): BookingTariffType[] => {
    const bookedTariffTypes: BookingTariffType[] = [];

    productBookingState.list.forEach((bookedProductType) => {
      if (isBookingProductTypeTariff(bookedProductType)) {
        bookedTariffTypes.push(bookedProductType);
      }
    });

    return bookedTariffTypes;
  }
);

/**
 * Get specified booked tariff type
 * @description Get booked tariff by specified ticketTypeId (package tariff types not included)
 * @param ticketTypeId
 * @returns Tariff type or null
 */
export const getBookedTariffTypeByTicketTypeId = (ticketTypeId: number) =>
  createSelector(getBookedTariffTypes, (bookedTariffTypes): BookingTariffType => {
    if (!bookedTariffTypes.length) {
      return null;
    }

    return bookedTariffTypes.find((bookedTariffType) => bookedTariffType.ticketTypeId === ticketTypeId) || null;
  });

/**
 * Get booked tariffs
 * @description Get bookings tariff (package tariff not included)
 * @returns Booked tariff or empty array
 */
export const getBookedTariffs = createSelector(
  getBookedTariffTypes,
  (bookedTariffTypes: BookingTariffType[]): BookingTariff[] => {
    const bookedTariffs: BookingTariff[] = [];

    bookedTariffTypes.forEach((bookedTariffType) => {
      bookedTariffType.tariffs.forEach((bookedTariff) => bookedTariffs.push(bookedTariff));
    });

    return bookedTariffs;
  }
);

/**
 * Get booked tariff by ticket person id
 * @description Get bookings tariff by specified ticketPersonId (package tariff not included)
 * @param ticketPersonId
 * @returns Booked tariff or null
 */
export const getBookedTariffByTicketPersonId = (ticketPersonId: number) =>
  createSelector(getBookedTariffs, (bookedTariffs: BookingTariff[]): BookingTariff => {
    if (!bookedTariffs.length) {
      return null;
    }

    return bookedTariffs.find((bookingTicket) => bookingTicket.ticketPersonId === ticketPersonId) || null;
  });

/**
 * Get booked tariff product type by ticket person id
 * @description Get booked tariff product type by specified ticketPersonId (package tariff not included)
 * @param ticketPersonId
 * @returns Booked tariff or empty booking tariff type
 */
export const getBookedTariffTypeAndTariffByTicketPersonId = (ticketPersonId: number) =>
  createSelector(getBookedTariffTypes, (bookedTariffTypes: BookingTariffType[]): BookingTariffType => {
    const bookedTariffType = bookedTariffTypes.find((bookedTariffType) =>
      bookedTariffType.tariffs.some((bookingTariff) => bookingTariff.ticketPersonId === ticketPersonId)
    );

    if (!bookedTariffType) {
      return null;
    }

    const bookedTariffTypeClone: BookingTariffType = cloneDeep(bookedTariffType);

    bookedTariffTypeClone.tariffs = bookedTariffTypeClone.tariffs.filter(
      (bookedTariff) => bookedTariff.ticketPersonId === ticketPersonId
    );

    return bookedTariffTypeClone;
  });

/**
 * Get booked tariff product type by ticket person id and voucher code
 * @description Get booked tariff product type by specified ticketPersonId (package tariff not included) and voucher code (not required)
 * @param ticketPersonId
 * @returns Booked tariff or empty booking tariff type
 */
export const getBookedTariffTypeAndTariffByTicketPersonIdAndVoucherCode = (
  ticketPersonId: number,
  voucherCode?: VoucherCode
) =>
  createSelector(getBookedTariffTypes, (bookedTariffTypes: BookingTariffType[]): BookingTariffType => {
    const bookedTariffType = bookedTariffTypes.find((bookedTariffType) =>
      bookedTariffType.tariffs.some((bookingTariff) => bookingTariff.ticketPersonId === ticketPersonId)
    );

    if (!bookedTariffType) {
      return null;
    }

    const bookedTariffTypeClone: BookingTariffType = cloneDeep(bookedTariffType);

    bookedTariffTypeClone.tariffs = bookedTariffTypeClone.tariffs.filter(
      (bookedTariff) => bookedTariff.ticketPersonId === ticketPersonId && bookedTariff.voucherCode === voucherCode
    );

    return bookedTariffTypeClone;
  });
